/**
 * Permissions Module
 * 권한 관리 전용 Vuex 모듈
 */

export default {
  namespaced: true,

  state: {
    roles: [], // 전체 역할 목록
    pages: [], // 전체 페이지 목록
    rolePermissions: {}, // role_idx별 페이지 권한 매핑
    userPermissions: {}, // employee_idx별 사용자 개별 권한(오버라이드) 매핑
    loading: false,
  },

  mutations: {
    setRoles(state, roles) {
      state.roles = roles;
    },

    setPages(state, pages) {
      state.pages = pages;
    },

    setRolePermissions(state, { roleIdx, permissions }) {
      state.rolePermissions[roleIdx] = permissions;
    },

    setUserPermissions(state, { employeeIdx, permissions }) {
      state.userPermissions[employeeIdx] = permissions;
    },

    setLoading(state, loading) {
      state.loading = loading;
    },

    addRole(state, role) {
      state.roles.push(role);
    },

    updateRole(state, updatedRole) {
      const index = state.roles.findIndex(r => r.role_idx === updatedRole.role_idx);
      if (index !== -1) {
        state.roles.splice(index, 1, updatedRole);
      }
    },

    addPage(state, page) {
      state.pages.push(page);
    },

    updatePage(state, updatedPage) {
      const index = state.pages.findIndex(p => p.page_idx === updatedPage.page_idx);
      if (index !== -1) {
        state.pages.splice(index, 1, updatedPage);
      }
    },
  },

  actions: {
    /**
     * 전체 역할 목록 조회
     */
    async loadRoles({ commit }) {
      commit('setLoading', true);
      try {
        const response = await this.$axios({
          method: 'get',
          url: '/roles/pagePermission/roles',
          loadingBar: true,
        });
        commit('setRoles', response.data.roles || []);
        return response.data.roles;
      } catch (error) {
        console.error('Failed to load roles:', error);
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },

    /**
     * 전체 페이지 목록 조회
     */
    async loadPages({ commit }) {
      commit('setLoading', true);
      try {
        const response = await this.$axios({
          method: 'get',
          url: '/roles/pagePermission/pages',
          loadingBar: true,
        });
        commit('setPages', response.data.pages || []);
        return response.data.pages;
      } catch (error) {
        console.error('Failed to load pages:', error);
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },

    /**
     * 특정 역할의 페이지 권한 조회
     */
    async loadRolePermissions({ commit }, roleIdx) {
      commit('setLoading', true);
      try {
        const response = await this.$axios({
          method: 'get',
          url: `/roles/pagePermission/roles/${roleIdx}/pages`,
          loadingBar: true,
        });
        commit('setRolePermissions', {
          roleIdx,
          permissions: response.data.pages || [],
        });
        return response.data.pages;
      } catch (error) {
        console.error(`Failed to load permissions for role ${roleIdx}:`, error);
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },

    /**
     * 역할별 페이지 권한 수정
     */
    async updateRolePermissions({ dispatch }, { roleIdx, pagePermissions }) {
      try {
        console.log('📤 권한 업데이트 요청:', {
          roleIdx,
          pagePermissionsCount: pagePermissions.length,
          url: `/roles/pagePermission/roles/${roleIdx}/pages`,
          data: { page_permissions: pagePermissions }
        });

        const response = await this.$axios({
          method: 'put',
          url: `/roles/pagePermission/roles/${roleIdx}/pages`,
          data: { page_permissions: pagePermissions },
          loadingBar: true,
        });

        console.log('✅ 권한 업데이트 성공:', response.data);

        // 수정 후 다시 로드
        await dispatch('loadRolePermissions', roleIdx);
      } catch (error) {
        console.error('❌ Failed to update role permissions:', error);

        // 상세 에러 정보 출력
        if (error.response) {
          console.error('🔴 서버 응답 상태:', error.response.status);
          console.error('🔴 서버 응답 데이터:', error.response.data);
          console.error('🔴 서버 응답 헤더:', error.response.headers);
          console.error('🔴 요청 URL:', error.config?.url);
          console.error('🔴 요청 데이터:', error.config?.data);
        } else if (error.request) {
          console.error('🔴 요청이 전송되었으나 응답 없음:', error.request);
        } else {
          console.error('🔴 요청 설정 중 오류:', error.message);
        }

        throw error;
      }
    },

    /**
     * 특정 사용자(직원)의 개별 페이지 권한(오버라이드) 조회
     * 역할 권한과 별개로 해당 사용자에게만 적용되는 허용/차단 목록
     */
    async loadUserPermissions({ commit }, employeeIdx) {
      commit('setLoading', true);
      try {
        const response = await this.$axios({
          method: 'get',
          url: `/roles/pagePermission/employees/${employeeIdx}/pages`,
          loadingBar: true,
        });
        commit('setUserPermissions', {
          employeeIdx,
          permissions: response.data.pages || [],
        });
        return response.data.pages;
      } catch (error) {
        console.error(`Failed to load permissions for employee ${employeeIdx}:`, error);
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },

    /**
     * 특정 사용자(직원)의 개별 페이지 권한(오버라이드) 저장
     * pagePermissions에 포함된 페이지만 오버라이드로 저장되고,
     * 포함되지 않은 페이지는 역할 기본 권한을 따름 (전체 교체 방식)
     */
    async updateUserPermissions({ dispatch }, { employeeIdx, pagePermissions }) {
      try {
        await this.$axios({
          method: 'put',
          url: `/roles/pagePermission/employees/${employeeIdx}/pages`,
          data: { page_permissions: pagePermissions },
          loadingBar: true,
        });

        // 수정 후 다시 로드
        await dispatch('loadUserPermissions', employeeIdx);
      } catch (error) {
        console.error('Failed to update user permissions:', error);
        throw error;
      }
    },

    /**
     * 직원 역할 변경
     */
    // eslint-disable-next-line no-unused-vars
    async updateEmployeeRole({ commit }, { employeeIdx, roleIdx }) {
      try {
        await this.$axios({
          method: 'patch',
          url: `/roles/pagePermission/employees/${employeeIdx}/role`,
          data: { role_idx: roleIdx },
          loadingBar: true,
        });
      } catch (error) {
        console.error('Failed to update employee role:', error);
        throw error;
      }
    },

    /**
     * 새 역할 생성
     */
    async createRole({ commit }, roleData) {
      try {
        const response = await this.$axios({
          method: 'post',
          url: '/roles/pagePermission/roles',
          data: roleData,
          loadingBar: true,
        });
        commit('addRole', response.data.role);
        return response.data.role;
      } catch (error) {
        console.error('Failed to create role:', error);
        throw error;
      }
    },

    /**
     * 역할 수정
     */
    async updateRole({ commit }, { roleIdx, roleData }) {
      try {
        const response = await this.$axios({
          method: 'put',
          url: `/roles/pagePermission/roles/${roleIdx}`,
          data: roleData,
          loadingBar: true,
        });
        commit('updateRole', response.data.role);
        return response.data.role;
      } catch (error) {
        console.error('Failed to update role:', error);
        throw error;
      }
    },

    /**
     * 새 페이지 등록
     */
    async createPage({ commit }, pageData) {
      try {
        const response = await this.$axios({
          method: 'post',
          url: '/roles/pagePermission/pages',
          data: pageData,
          loadingBar: true,
        });
        commit('addPage', response.data.page);
        return response.data.page;
      } catch (error) {
        console.error('Failed to create page:', error);
        throw error;
      }
    },

    /**
     * 페이지 정보 수정
     */
    async updatePage({ commit }, { pageIdx, pageData }) {
      try {
        const response = await this.$axios({
          method: 'put',
          url: `/roles/pagePermission/pages/${pageIdx}`,
          data: pageData,
          loadingBar: true,
        });
        commit('updatePage', response.data.page);
        return response.data.page;
      } catch (error) {
        console.error('Failed to update page:', error);
        throw error;
      }
    },
  },

  getters: {
    /**
     * role_code로 역할 찾기
     */
    getRoleByCode: (state) => (code) => {
      return state.roles.find(r => r.role_code === code);
    },

    /**
     * 카테고리별 페이지 목록
     */
    getPagesByCategory: (state) => (category) => {
      return state.pages.filter(p => p.page_category === category);
    },

    /**
     * 페이지 카테고리 목록 (중복 제거)
     */
    getCategories: (state) => {
      const categories = state.pages.map(p => p.page_category);
      return [...new Set(categories)].filter(Boolean).sort();
    },

    /**
     * 특정 역할의 권한 체크
     */
    hasRolePermission: (state) => (roleIdx, pageUri) => {
      const permissions = state.rolePermissions[roleIdx];
      if (!permissions) return false;

      return permissions.some(
        perm => perm.page_uri === pageUri && perm.can_access === 'Y'
      );
    },

    /**
     * 활성화된 역할 목록만
     */
    getActiveRoles: (state) => {
      return state.roles.filter(r => r.is_active === 'Y');
    },

    /**
     * 활성화된 페이지 목록만
     */
    getActivePages: (state) => {
      return state.pages.filter(p => p.is_active === 'Y');
    },
  },
};
